import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import CanonicalTag from "@/components/CanonicalTag";
import luxuryGuestHouseImage from "@/assets/luxury-guest-house-adu.jpg";

const HowToUseADUGuestHouseLuxury = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-guest-house-luxury" />
      <Navigation />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU as a Luxury Guest House
          </h1>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Host Guests in Style & Comfort
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-3xl mx-auto">
            Create a luxury ADU guest house that combines high-end design with practical amenities.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-[#E4683B] hover:bg-[#E4683B]/90 text-white px-8 py-3 text-lg"
            >
              📅 Book & Schedule
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-white bg-[#E4683B] hover:bg-[#E4683B]/90 px-8 py-3 text-lg"
              onClick={() => window.location.href = '/landing'}
            >
              📐 What fits my lot?
            </Button>
          </div>
        </div>

        {/* Main Image */}
        <div className="mb-16">
          <img 
            src={luxuryGuestHouseImage} 
            alt="Luxury ADU guest house with premium finishes" 
            className="w-full max-w-4xl mx-auto rounded-lg shadow-lg"
          />
        </div>

        {/* Features That Impress Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Features That Impress
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Private entrance, spacious patio, premium finishes</h3>
              <p className="text-muted-foreground">Complete independence with luxury touches throughout.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">En-suite bathroom with modern fixtures</h3>
              <p className="text-muted-foreground">Spa-like bathroom experience with high-end amenities.</p>
            </div>
            <div className="bg-card p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Smart home integration</h3>
              <p className="text-muted-foreground">Advanced lighting, locks, and climate control systems.</p>
            </div>
          </div>
        </div>

        {/* Mid-Section CTAs */}
        <div className="text-center mb-16">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            variant="outline" 
            className="border-[#E4683B] text-white bg-[#E4683B] hover:bg-[#E4683B]/90 px-8 py-3"
            onClick={() => window.location.href = '/landing'}
          >
            📐 What fits my lot?
          </Button>
          <Button 
            className="bg-white text-[#E4683B] border border-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
            onClick={() => window.location.href = '/adu-calculator'}
          >
            💰 Get Price & ROI Report
          </Button>
        </div>
        </div>

        {/* Ideal For Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Ideal For:
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground text-center">
              Family visits, upscale Airbnb guests, in-laws, or VIP stays
            </p>
          </div>
        </div>

        {/* Returns That Pay Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>
            Returns That Pay
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-muted-foreground text-center">
              Luxury ADUs generate higher short-term rental income and attract design-conscious homebuyers.
            </p>
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
              onClick={() => window.open('/our-work', '_blank')}
            >
              🏡 Our Portfolio
            </Button>
            <Button 
              variant="outline" 
              className="border-[#E4683B] text-[#E4683B] hover:bg-[#E4683B] hover:text-white px-8 py-3"
              onClick={() => window.open('/faq', '_blank')}
            >
              ❓ Our FAQ
            </Button>
          </div>
        </div>
      </div>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl max-h-[80vh] p-0">
          <div className="relative w-full h-[600px]">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
            >
              ✕
            </button>
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUGuestHouseLuxury;